import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Paper,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { categories } from '../services/api';

interface Category {
    category_id: number;
    name: string;
    created_by: number;
}

export const CategoryList: React.FC = () => {
    const [categoryList, setCategoryList] = useState<Category[]>([]);
    const [open, setOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [categoryName, setCategoryName] = useState('');

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await categories.getAll();
            setCategoryList(response.data);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const handleOpen = (category?: Category) => {
        if (category) {
            setEditingCategory(category);
            setCategoryName(category.name);
        } else {
            setEditingCategory(null);
            setCategoryName('');
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingCategory(null);
        setCategoryName('');
    };

    const handleSubmit = async () => {
        try {
            if (categoryName.trim() === '') {
                alert("Category name cannot be empty.");
                return;
            }
            if (editingCategory) {
                const res = await categories.update(editingCategory.category_id, { name: categoryName });

                if (res.status != 200) {
                    alert(res.data.message || "Failed to update category.");
                    return;
                }

                alert("Category updated successfully!");
            } else {
                const res = await categories.create({ name: categoryName });
                
                if (res.status != 201) {
                    alert(res.data.message || "Failed to create category.");
                    return;
                }

                alert("Category created successfully!");
            }
            handleClose();
            loadCategories();
        } catch (error) {
            console.error('Failed to save category:', error);
            alert("Failed to save category. Please try again.");
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await categories.delete(id);
                loadCategories();
            } catch (error) {
                console.error('Failed to delete category:', error);
            }
        }
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
                sx={{
                    mb: 3,
                    borderRadius: '13px',
                    textTransform: 'none',
                }}
            >
                Add Category
            </Button>

            <Paper sx={{ borderRadius: '13px' }}>
                <List>
                    {categoryList.map((category) => (
                        <ListItem key={category.category_id}>
                            <ListItemText
                                primary={category.name}
                                secondary={category.created_by === 1 ? '(Default)' : ''}
                            />
                            {category.created_by !== 1 && (
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleOpen(category)}
                                        sx={{ mr: 1 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleDelete(category.category_id)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    ))}
                </List>
            </Paper>

            <Dialog open={open} onClose={handleClose} sx={{ '& .MuiDialog-paper': { borderRadius: '17px' } }}>
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Category Name"
                        fullWidth
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '13px' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '13px', textTransform: 'none', py: 1.2 }} >
                        {editingCategory ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
